import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, ExternalLink, Copy, Check, Pencil, Eye, Send, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentRefresh, QUERY_KEYS } from '../ai/hooks';
import { getForms, ensureDefaultContactForm } from '../services/formService';
import { getFormAnalytics } from '../services/analyticsService';
import type { Form } from '../types/forms';
import type { FormAnalytics } from '../types/analytics';
import { logger } from '../lib/logger';

export function Forms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState<Form[]>([]);
  const [formAnalytics, setFormAnalytics] = useState<Record<string, FormAnalytics>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadForms = useCallback(async () => {
    if (!user) return;
    try {
      // Ensure default contact form exists
      await ensureDefaultContactForm(user.id);

      const data = await getForms(user.id);
      setForms(data);

      const analyticsMap: Record<string, FormAnalytics> = {};
      for (const form of data) {
        const analytics = await getFormAnalytics(user.id, form.id, form.name);
        analyticsMap[form.id] = analytics;
      }
      setFormAnalytics(analyticsMap);
    } catch (error) {
      logger.error('Error loading forms:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadForms();
    }
  }, [user, loadForms]);

  // Listen for AI Agent form updates
  useAgentRefresh([QUERY_KEYS.FORMS], loadForms);

  const copyPublicUrl = async (form: Form) => {
    const url = `${window.location.origin}/f/${form.slug}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(form.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white">Formulare</h1>
          <p className="text-[#6B6B6B] dark:text-slate-400 mt-1">
            Erstelle und verwalte deine Lead-Formulare
          </p>
        </div>
        <Link
          data-tour="create-form"
          to="/forms/new"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          Formular erstellen
        </Link>
      </div>

      {forms.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Noch keine Formulare vorhanden
          </h3>
          <p className="text-gray-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Erstelle dein erstes Formular, um Leads von deiner Website oder Landingpages zu sammeln.
          </p>
          <Link
            to="/forms/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Erstes Formular erstellen
          </Link>
        </div>
      ) : (
        <div data-tour="form-list" className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="px-6 py-3 bg-gray-50 dark:bg-slate-700 border-b border-gray-100 dark:border-slate-600">
            <p className="text-xs text-gray-500 dark:text-slate-400">Statistiken der letzten 7 Tage</p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-slate-700">
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-slate-400">
                  Name
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-slate-400">
                  Status
                </th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-slate-400">
                  <div className="flex items-center justify-center gap-1">
                    <Eye className="w-3.5 h-3.5" />
                    Aufrufe
                  </div>
                </th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-slate-400">
                  <div className="flex items-center justify-center gap-1">
                    <Send className="w-3.5 h-3.5" />
                    Einsendungen
                  </div>
                </th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-slate-400">
                  <div className="flex items-center justify-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" />
                    Conversion
                  </div>
                </th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-slate-400">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => {
                const analytics = formAnalytics[form.id];
                return (
                  <tr
                    key={form.id}
                    className="border-b border-gray-50 dark:border-slate-700 last:border-0 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                    onClick={() => navigate(`/forms/${form.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 dark:text-white">{form.name}</p>
                          {form.is_system && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                              Standard
                            </span>
                          )}
                        </div>
                        {form.description && (
                          <p className="text-sm text-gray-500 dark:text-slate-400 truncate max-w-xs">
                            {form.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                          form.is_published
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-slate-300'
                        }`}
                      >
                        {form.is_published ? 'Veröffentlicht' : 'Entwurf'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {analytics?.views || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {analytics?.submissions || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        {analytics?.viewToSubmitConversion || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="flex items-center justify-end gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {form.is_published && (
                          <>
                            <a
                              href={`/f/${form.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Formular öffnen"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => copyPublicUrl(form)}
                              className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Link kopieren"
                            >
                              {copiedId === form.id ? (
                                <Check className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => navigate(`/forms/${form.id}`)}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
